import { MutationPayload } from 'vuex';
import { Store as State } from './store';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { Tracking, TrackingDefinition, PKG_VERSION } from './AbstractTracking';

const PLUGININSTANCE_ID = 'wctcc3';
const LIFECYCLE_CHANNEL = `${PLUGININSTANCE_ID.toUpperCase()}-LIFECYCLE`;

type EventType = 'pageview' | 'click';
type EventAction = 'first_interaction' | 'engagement' | 'charging_time_calculator';
interface EuropeTrackingEvent {
  event: EventType;
  application: ApplicationData;
  eventDetail: EventDetail;
}
type EventCategory = 'feature' | 'impression';
interface EventDetail {
  category: EventCategory;
  action: EventAction;
  label: string;
  vehicle: {
    modelName: string;
    baumusterId: string;
  };
}

interface ApplicationData {
  id: 'eq';
  configId: 'ctc';
  version: string;
}

interface PreFillObject {
  category: EventCategory;
  event: EventType;
  action: EventAction;
  label: string;
  state: State;
}

const staticData: { application: ApplicationData } = {
  application: {
    id: 'eq',
    configId: 'ctc',
    version: PKG_VERSION,
  },
};

type TrackingLabel = string;

const ENGAGEMENT_EVENTS_COUNT = 5;

const TrackableEvents: TrackingDefinition = {
  'chargingData/setSelectedModel': {
    label: 'model',
    alias: 'm',
    payloadPath: 'payload.className',
  },
  'chargingData/setSelectedVehicle': {
    label: 'vehicle',
    alias: 'v',
    payloadPath: 'payload.name',
  },
  'chargingData/setBatteryCharging': {
    label: 'BatteryChargingChanged',
    alias: 'bcc',
    payloadPath: 'payload',
  },
};

export class EuropeTracking extends Tracking<EuropeTrackingEvent, State> {
  private trackingsLabels: TrackingLabel[] = [];
  private chargingCalculatorTracker: Subject<TrackingLabel>;
  private trackingEventWrapper: EuropeTrackingEvent | null = null;
  private eventListTracker: Subject<TrackingLabel[]>;

  constructor() {
    super(TrackableEvents, LIFECYCLE_CHANNEL, PLUGININSTANCE_ID);
    this.chargingCalculatorTracker = new Subject<TrackingLabel>();

    this.chargingCalculatorTracker.pipe(debounceTime(500)).subscribe((label) => {
      this.addEvent(label);
    });

    this.eventListTracker = new Subject<TrackingLabel[]>();
    this.eventListTracker.pipe(filter((events) => events.length === ENGAGEMENT_EVENTS_COUNT)).subscribe((events) => {
      this.callEngagementEvents(events);
    });
    this.eventListTracker.pipe(filter((events) => events.length === 1)).subscribe((events) => {
      this.callFirstEvent(events[0]);
    });
  }

  public trackMiddleware = (mutation: MutationPayload, state: State): void => {
    const trackInfos = this.toTrackActions[mutation.type];
    if (trackInfos && this.trackingsLabels.length < ENGAGEMENT_EVENTS_COUNT + 1) {
      this.trackingEventWrapper = this.getPreFilledEvent({
        category: 'feature',
        action: 'engagement',
        event: 'click',
        label: '',
        state,
      });
      switch (mutation.type) {
        case 'chargingData/setBatteryCharging': {
          const value = this.getPayloadByStringDefinition<string>(mutation, trackInfos.payloadPath);
          this.chargingCalculatorTracker.next(`${trackInfos.alias}:${value}`);
          break;
        }
        default:
          this.addEvent(`${trackInfos.alias}:${this.getPayloadByStringDefinition(mutation, trackInfos.payloadPath)}`);
          break;
      }
    }
  };

  private getPreFilledEvent(input: PreFillObject): EuropeTrackingEvent {
    return {
      application: staticData.application,
      eventDetail: {
        vehicle: {
          modelName: input.state.chargingData.selectedVehicle?.name ?? '',
          // oneweb tracking needs the first 6 digits of the baumuster ID.
          // baumusterID is usually defined as:
          // digits 0-3: vehicle class (e.g. EQC)
          // digits 4-5: model (e.g. 400 4MATIC)
          // digit 6: model variant (e.g. AMG Edition)
          baumusterId: input.state.chargingData.selectedVehicle?.typeId.split('-')[0].substr(0, 6) ?? '',
        },
        action: input.action,
        label: input.label,
        category: input.category,
      },
      event: input.event,
    };
  }

  private addEvent(label: TrackingLabel) {
    this.trackingsLabels.push(label);
    this.eventListTracker.next(this.trackingsLabels);
  }

  private getTrackingEventFilledWithLabels(labels: TrackingLabel[]): EuropeTrackingEvent | null {
    let label = labels.join(',');
    if (labels.length > 1) {
      label = `${labels.length}.${label}`;
    }
    if (this.trackingEventWrapper) {
      this.trackingEventWrapper.eventDetail.label = label;
      return this.trackingEventWrapper;
    }
    return null;
  }

  private callFirstEvent(label: TrackingLabel) {
    const event = this.getTrackingEventFilledWithLabels([label]);
    if (event) {
      event.eventDetail.action = 'first_interaction';
      this.publishTrackEvent(event);
    }
  }

  private callEngagementEvents(labels: TrackingLabel[]) {
    const event = this.getTrackingEventFilledWithLabels(labels);
    if (event) {
      this.publishTrackEvent(event);
    }
  }
}
